import toEchartData from './dataProcessor';
import tooltipFormatter from './tooltipFormatter';

/**
 * O `dataProcessor` produz nodes onde `category === 0` é a pergunta principal e
 * `category === 1..N` são as perguntas correlacionadas. Para que o pipeline padrão de
 * cores (`option.color[i]` indexado por `legend.data[i]`) funcione idêntico aos demais
 * gráficos da correlação, fazemos um remap aqui:
 *
 *   - Pergunta principal: removemos a `category` e pintamos a bolha com `mainColor`
 *     via `itemStyle.color`. Assim ela não disputa slot no array de cores.
 *   - Perguntas correlacionadas: deslocamos `category` em -1 e ficam alinhadas com
 *     `legend.data` e com `option.color` no mesmo índice.
 *
 * Em troca de uma reindexação trivial dos nodes, o color picker e o ThemeManager
 * tratam o graph_bubble com a mesma semântica do treemap.
 */
function remapForCorrelationPipeline(echartData, mainColor, perguntaPrincipalEnunciado) {
  const nodes = echartData.nodes.map((node) => {
    if (node.category === 0) {
      // Bolha da pergunta principal — sem categoria; cor fixa via itemStyle.
      const { category, ...rest } = node;
      return {
        ...rest,
        itemStyle: { ...(rest.itemStyle || {}), color: mainColor },
      };
    }
    return { ...node, category: node.category - 1 };
  });

  // A primeira categoria do `dataProcessor` é a da pergunta principal — descartamos.
  const categories = echartData.categories.filter(
    (cat) => cat && cat.name !== perguntaPrincipalEnunciado,
  );

  return { ...echartData, nodes, categories };
}

/**
 * Aplica o efeito de highlit em todos os nodes da categoria selecionada.
 */
function applyHighlitToCategory(nodes, highlitCategory, highlitTheme) {
  if (typeof highlitCategory !== 'number' || highlitCategory < 0 || !highlitTheme) return nodes;
  return nodes.map((node) => {
    const isMatch = node.category === highlitCategory;
    if (!isMatch) {
      // Limpa borda residual sem perder a cor de fundo (ex: mainColor da principal).
      if (node.itemStyle && (node.itemStyle.borderColor || node.itemStyle.borderWidth || node.itemStyle.borderType)) {
        const { borderType, borderColor, borderWidth, ...keep } = node.itemStyle;
        return { ...node, itemStyle: keep };
      }
      return node;
    }
    return {
      ...node,
      itemStyle: {
        ...(node.itemStyle || {}),
        borderType: highlitTheme.borderType,
        borderColor: highlitTheme.borderColor,
        borderWidth: highlitTheme.borderWidth,
      },
    };
  });
}

function getSeries(remapped, themeColors, fontSize, highlitTheme, defaultZoom = 1) {
  const { nodes, links, categories } = remapped;

  const highlitCategory = highlitTheme ? highlitTheme.categoryIndex : -1;

  return [
    {
      type: 'graph',
      layout: 'force',
      animation: false,
      label: {
        position: 'inside',
        formatter: '{b}',
        fontSize,
        rich: {
          name: {
            textBorderColor: '#fff',
          },
        },
      },
      draggable: true,
      data: applyHighlitToCategory(nodes, highlitCategory, highlitTheme),
      categories,
      focusNodeAdjacency: true,
      lineStyle: {
        width: 0.7,
        opacity: 0.7,
        curveness: 0.3,
      },
      emphasis: {
        lineStyle: {
          width: 15,
        },
      },
      zoom: defaultZoom,
      roam: 'move',
      force: {
        edgeLength: 20,
        repulsion: 30,
        gravity: 0.2,
        layoutAnimation: true,
      },
      edges: links,
      // O `option.color` global do echarts não chega às séries do graph quando elas têm
      // `categories`; por isso replicamos aqui no nível da série para o setOption de
      // updateOnly:colors propagar de forma consistente.
      color: themeColors,
    },
  ];
}

/**
 * Constrói o echart option do grafo de bolhas de correlação no contrato consumido pelo
 * ChartManager.
 *
 * @param {object} data Dado bruto da correlação
 * @param {{colors: string[], highlitedItemBorderType?, highlitedItemBorderColor?, highlitedItemBorderWidth?, appThemeColors?: string[]}} colors Tema parsado
 * @param {number} fontSize Tamanho de fonte (label / legenda)
 * @param {Array<string>} _disabledLegends (não usado por enquanto)
 * @param {number} highlitIndex Índice da pergunta correlacionada highlightada
 */
export default function toGraphBubbleEchartOption(data, colors, fontSize, _disabledLegends, highlitIndex) {
  const echartData = toEchartData(data);
  const perguntas = echartData.legends;

  const themeColors = (colors && colors.colors) || [];
  // Pergunta principal usa a primeira cor do tema da aplicação (ou a primeira cor
  // calculada, como fallback). Fixa: não participa do ciclo de configuração.
  const mainColor = (colors && colors.appThemeColors && colors.appThemeColors[0])
    || themeColors[0]
    || '#000';

  const remapped = remapForCorrelationPipeline(echartData, mainColor, data && data.enunciado);

  const highlitTheme = (typeof highlitIndex === 'number' && highlitIndex >= 0)
    ? {
      categoryIndex: highlitIndex,
      borderType: colors && colors.highlitedItemBorderType,
      borderColor: colors && colors.highlitedItemBorderColor,
      borderWidth: colors && colors.highlitedItemBorderWidth,
    }
    : null;

  // O array `colors` que o ChartManager injeta carrega também o tema completo —
  // usamos seus subtemas de `legend` para manter as cores da paginação e do texto
  // alinhadas ao tema da aplicação (light/dark).
  const legendTheme = (colors && colors.legend) || {};
  const legendPagination = legendTheme.pagination || {};

  return {
    color: themeColors,
    visibleLegends: perguntas,
    tooltip: {
      formatter: tooltipFormatter,
      confine: true,
    },
    legend: {
      // Lista canônica de perguntas correlacionadas (sem incluir a principal),
      // consumida pelos resolvers de color picker, por `useSeries` e pela própria
      // legenda visível no topo do gráfico.
      data: perguntas,
      type: 'scroll',
      textStyle: {
        color: legendTheme.color,
        fontSize,
      },
      pageTextStyle: {
        color: legendPagination.text && legendPagination.text.color,
      },
      pageIconColor: legendPagination.buttons && legendPagination.buttons.color,
    },
    series: getSeries(remapped, themeColors, fontSize, highlitTheme),
  };
}
